import type {
  ModuleGovernanceModule,
  ModuleGovernanceRouteGroup,
  ModuleGovernanceState,
} from '@triedge/staff-settings-domain';

export type GovernanceScope = 'school' | 'parent';
export type ParentDependencyMode = 'school-module' | 'shared-data-source' | 'none';
export type ModuleGovernanceDisableBehavior = 'hide-and-deny' | 'hide-sidebar-only';

export type ModuleGovernanceRouteGroupCatalog = {
  routeGroupId: string;
  label: string;
  routePrefixes: string[];
  defaultEnabled?: boolean;
};

export type ModuleGovernanceModuleCatalog = {
  moduleId: string;
  label: string;
  scope: GovernanceScope;
  summary: string;
  routePrefixes: string[];
  routeGroups: ModuleGovernanceRouteGroupCatalog[];
  defaultEnabled?: boolean;
  alwaysOn?: boolean;
  disableBehavior?: ModuleGovernanceDisableBehavior;
  sidebarNavId?: string;
  dependencyMode?: ParentDependencyMode;
  dependsOnSchoolModuleId?: string;
  retired?: boolean;
  showInEnablementUi?: boolean;
};

export type MaterializedModuleGovernanceRouteGroup = ModuleGovernanceRouteGroupCatalog & {
  enabled: boolean;
};

export type MaterializedModuleGovernanceModule = Omit<ModuleGovernanceModuleCatalog, 'routeGroups'> & {
  enabled: boolean;
  routeGroups: MaterializedModuleGovernanceRouteGroup[];
};

export type MaterializedModuleGovernanceState = {
  schoolModules: MaterializedModuleGovernanceModule[];
  parentModules: MaterializedModuleGovernanceModule[];
};

export const TEMP_MODULE_ID = 'temp';
export const SCHOOL_TEMP_ROUTE_GROUP_IDS = [
  'temp-academics',
  'temp-finance',
  'temp-substitutes',
  'temp-tuition-admin',
  'temp-school-camps',
  'temp-school-donations',
  'temp-school-tip-a-teacher',
] as const;
export const PARENT_TEMP_ROUTE_GROUP_IDS = [
  'temp-parent-tip-a-teacher',
  'temp-parent-donations',
  'temp-parent-parnes-hayom',
  'temp-parent-camps',
] as const;
const SESSION_FORCED_SCHOOL_MODULE_IDS = new Set(['tuition']);

const isSessionForcedSchoolModule = (
  catalogModule: ModuleGovernanceModuleCatalog,
): boolean =>
  catalogModule.scope === 'school'
  && SESSION_FORCED_SCHOOL_MODULE_IDS.has(catalogModule.moduleId);

const SCHOOL_MODULES: readonly ModuleGovernanceModuleCatalog[] = [
  {
    moduleId: 'settings',
    label: 'Settings',
    scope: 'school',
    summary: 'Core organization, staffing, and policy controls.',
    routePrefixes: ['/school/settings'],
    routeGroups: [],
    alwaysOn: true,
    sidebarNavId: 'settings',
    showInEnablementUi: false,
  },
  {
    moduleId: 'control-plane',
    label: 'Control Plane',
    scope: 'school',
    summary: 'Super-administrator operator tooling for governed diagnostics and platform operations.',
    routePrefixes: ['/super-administrator/control-plane'],
    routeGroups: [],
    alwaysOn: true,
    sidebarNavId: 'control-plane',
    showInEnablementUi: false,
  },
  {
    moduleId: 'run-reports',
    label: 'Run Reports',
    scope: 'school',
    summary: 'Template-driven reporting workspace across tuition, students, admissions, and financial-aid data.',
    routePrefixes: ['/school/run-reports'],
    routeGroups: [
      {
        routeGroupId: 'workspace',
        label: 'Workspace',
        routePrefixes: ['/school/run-reports'],
      },
    ],
    defaultEnabled: true,
    sidebarNavId: 'run-reports',
    showInEnablementUi: true,
  },
  {
    moduleId: 'admissions',
    label: 'Admissions',
    scope: 'school',
    summary: 'Applications, applicant review, and enrollment workflow.',
    routePrefixes: ['/school/admissions'],
    routeGroups: [
      {
        routeGroupId: 'dashboard',
        label: 'Dashboard',
        routePrefixes: ['/school/admissions/dashboard'],
      },
      {
        routeGroupId: 'applicant-detail',
        label: 'Applicant detail',
        routePrefixes: [
          '/school/admissions/children',
          '/school/admissions/family',
          '/school/admissions/documents',
          '/school/admissions/notes',
          '/school/admissions/tuition',
        ],
      },
    ],
    sidebarNavId: 'admissions',
  },
  {
    moduleId: 'attendance',
    label: 'Attendance',
    scope: 'school',
    summary: 'Attendance overview, new records, teacher preview, student history, export evidence, and v1.2 attendance contract operations.',
    routePrefixes: ['/school/attendance'],
    routeGroups: [
      {
        routeGroupId: 'overview',
        label: 'Overview',
        routePrefixes: ['/school/attendance'],
      },
      {
        routeGroupId: 'new-records',
        label: 'New Records',
        routePrefixes: ['/school/attendance/new-records'],
      },
      {
        routeGroupId: 'teacher-view',
        label: 'Teacher View',
        routePrefixes: ['/school/attendance/teacher-view'],
      },
      {
        routeGroupId: 'student-history',
        label: 'Student History',
        routePrefixes: ['/school/attendance/students'],
      },
    ],
    defaultEnabled: true,
    sidebarNavId: 'attendance',
    showInEnablementUi: true,
  },
  {
    moduleId: 'faculty',
    label: 'Faculty',
    scope: 'school',
    summary: 'Faculty directory, profile, and assignment surfaces.',
    routePrefixes: ['/school/faculty'],
    routeGroups: [
      {
        routeGroupId: 'directory',
        label: 'Directory',
        routePrefixes: ['/school/faculty'],
      },
      {
        routeGroupId: 'profiles',
        label: 'Profiles',
        routePrefixes: ['/school/faculty/profile'],
      },
    ],
    sidebarNavId: 'faculty',
  },
  {
    moduleId: 'financial-aid',
    label: 'Financial Aid',
    scope: 'school',
    summary: 'Financial-aid dashboard and staff application review.',
    routePrefixes: ['/school/financialaid'],
    routeGroups: [
      {
        routeGroupId: 'dashboard',
        label: 'Dashboard',
        routePrefixes: ['/school/financialaid/dashboard'],
      },
      {
        routeGroupId: 'staff-review',
        label: 'Staff review',
        routePrefixes: ['/school/financialaid/staff'],
      },
    ],
    sidebarNavId: 'financial-aid',
  },
  {
    moduleId: 'payroll',
    label: 'Payroll',
    scope: 'school',
    summary: 'Payroll prep dashboard, employee compensation detail, and export governance.',
    routePrefixes: ['/school/payroll'],
    routeGroups: [
      {
        routeGroupId: 'dashboard',
        label: 'Dashboard',
        routePrefixes: ['/school/payroll'],
      },
      {
        routeGroupId: 'employee-detail',
        label: 'Employee detail',
        routePrefixes: ['/school/payroll/employee'],
      },
    ],
    defaultEnabled: false,
    sidebarNavId: 'payroll',
    retired: true,
    showInEnablementUi: false,
  },
  {
    moduleId: 'tuition',
    label: 'Tuition',
    scope: 'school',
    summary: 'School tuition management and administrator tuition oversight.',
    routePrefixes: ['/school/tuition', '/administrator/tuition'],
    routeGroups: [
      {
        routeGroupId: 'school-dashboard',
        label: 'School dashboard',
        routePrefixes: ['/school/tuition'],
      },
      {
        routeGroupId: 'administrator-dashboard',
        label: 'Administrator dashboard',
        routePrefixes: ['/administrator/tuition'],
      },
    ],
    defaultEnabled: true,
    sidebarNavId: 'tuition',
    showInEnablementUi: true,
  },
  {
    moduleId: 'students-families',
    label: 'Students & Families',
    scope: 'school',
    summary: 'Family dashboard and family detail workflow.',
    routePrefixes: ['/school/students'],
    routeGroups: [
      {
        routeGroupId: 'dashboard',
        label: 'Dashboard',
        routePrefixes: ['/school/students/dashboard'],
      },
      {
        routeGroupId: 'family-detail',
        label: 'Family detail',
        routePrefixes: ['/school/students/family'],
      },
    ],
    sidebarNavId: 'students-families',
  },
  {
    moduleId: TEMP_MODULE_ID,
    label: 'Temp',
    scope: 'school',
    summary: 'Preview-only sidebar exposure for staged school and parent route families.',
    routePrefixes: [
      '/school/academics',
      '/school/finance',
      '/school/substitutes',
      '/administrator/tuition',
      '/school/camps',
      '/school/donations',
      '/school/tip-a-teacher',
    ],
    routeGroups: [
      {
        routeGroupId: 'temp-academics',
        label: 'Academics',
        routePrefixes: ['/school/academics'],
      },
      {
        routeGroupId: 'temp-finance',
        label: 'Finance',
        routePrefixes: ['/school/finance'],
      },
      {
        routeGroupId: 'temp-substitutes',
        label: 'Substitutes',
        routePrefixes: ['/school/substitutes'],
      },
      {
        routeGroupId: 'temp-tuition-admin',
        label: 'Tuition Admin',
        routePrefixes: ['/administrator/tuition'],
      },
      {
        routeGroupId: 'temp-school-camps',
        label: 'Camps',
        routePrefixes: ['/school/camps'],
      },
      {
        routeGroupId: 'temp-school-donations',
        label: 'Donations',
        routePrefixes: ['/school/donations'],
      },
      {
        routeGroupId: 'temp-school-tip-a-teacher',
        label: 'Tip a Teacher',
        routePrefixes: ['/school/tip-a-teacher'],
      },
    ],
    defaultEnabled: false,
    disableBehavior: 'hide-sidebar-only',
    showInEnablementUi: true,
  },
] as const;

const PARENT_MODULES: readonly ModuleGovernanceModuleCatalog[] = [
  {
    moduleId: 'dashboard',
    label: 'Dashboard',
    scope: 'parent',
    summary: 'Parent landing page, family status highlights, and dashboard widgets.',
    dependencyMode: 'none',
    routePrefixes: [
      '/parent/portal/dashboard',
      '/portal/family/dashboard',
      '/portal/family/parent/dashboard',
    ],
    routeGroups: [
      {
        routeGroupId: 'dashboard',
        label: 'Dashboard',
        routePrefixes: [
          '/parent/portal/dashboard',
          '/portal/family/dashboard',
          '/portal/family/parent/dashboard',
        ],
      },
    ],
    sidebarNavId: 'dashboard',
  },
  {
    moduleId: 'applications',
    label: 'Applications',
    scope: 'parent',
    summary: 'Application intake and status tracking.',
    dependencyMode: 'none',
    routePrefixes: ['/parent/portal/applications', '/parent/portal--new/applications'],
    routeGroups: [
      {
        routeGroupId: 'applications',
        label: 'Applications',
        routePrefixes: ['/parent/portal/applications', '/parent/portal--new/applications'],
      },
    ],
    sidebarNavId: 'applications',
  },
  {
    moduleId: 'enrollment',
    label: 'Enrollment',
    scope: 'parent',
    summary: 'Enrollment forms and post-acceptance progression.',
    dependencyMode: 'school-module',
    dependsOnSchoolModuleId: 'admissions',
    routePrefixes: ['/parent/portal/enrollment', '/parent/portal--new/enrollment'],
    routeGroups: [
      {
        routeGroupId: 'enrollment',
        label: 'Enrollment',
        routePrefixes: ['/parent/portal/enrollment', '/parent/portal--new/enrollment'],
      },
    ],
    sidebarNavId: 'enrollment',
  },
  {
    moduleId: 'financial-aid',
    label: 'Financial Aid',
    scope: 'parent',
    summary: 'Parent scholarship application and award visibility.',
    dependencyMode: 'school-module',
    dependsOnSchoolModuleId: 'financial-aid',
    routePrefixes: [
      '/parent/portal/financialaid',
      '/parent/portal/financial-aid',
      '/parent/portal--new/financialaid',
      '/parent/portal--new/financial-aid',
    ],
    routeGroups: [
      {
        routeGroupId: 'financial-aid',
        label: 'Financial Aid',
        routePrefixes: [
          '/parent/portal/financialaid',
          '/parent/portal/financial-aid',
          '/parent/portal--new/financialaid',
          '/parent/portal--new/financial-aid',
        ],
      },
    ],
    sidebarNavId: 'financial-aid',
  },
  {
    moduleId: 'calendar',
    label: 'Calendar',
    scope: 'parent',
    summary: 'Parent calendar and upcoming schedule visibility.',
    dependencyMode: 'shared-data-source',
    routePrefixes: ['/parent/portal/calendar'],
    routeGroups: [
      {
        routeGroupId: 'calendar',
        label: 'Calendar',
        routePrefixes: ['/parent/portal/calendar'],
      },
    ],
    sidebarNavId: 'calendar',
  },
  {
    moduleId: 'tuition',
    label: 'Tuition',
    scope: 'parent',
    summary: 'Parent payment status, balances, and statement history.',
    dependencyMode: 'school-module',
    dependsOnSchoolModuleId: 'tuition',
    routePrefixes: ['/parent/portal/tuition'],
    routeGroups: [
      {
        routeGroupId: 'tuition',
        label: 'Tuition',
        routePrefixes: ['/parent/portal/tuition'],
      },
    ],
    defaultEnabled: false,
    sidebarNavId: 'tuition',
  },
  {
    moduleId: 'parent-attendance',
    label: 'Attendance',
    scope: 'parent',
    summary: 'Existing parent attendance visibility; future consumer outside the Attendance v1.2 admin MVP package.',
    dependencyMode: 'school-module',
    dependsOnSchoolModuleId: 'students-families',
    routePrefixes: ['/parent/portal/attendance'],
    routeGroups: [
      {
        routeGroupId: 'attendance',
        label: 'Attendance',
        routePrefixes: ['/parent/portal/attendance'],
      },
    ],
    sidebarNavId: 'attendance',
  },
  {
    moduleId: 'academic',
    label: 'Academic',
    scope: 'parent',
    summary: 'Academic performance and report access.',
    dependencyMode: 'school-module',
    dependsOnSchoolModuleId: 'students-families',
    routePrefixes: ['/parent/portal/academic'],
    routeGroups: [
      {
        routeGroupId: 'academic',
        label: 'Academic',
        routePrefixes: ['/parent/portal/academic'],
      },
    ],
    sidebarNavId: 'academic',
  },
  {
    moduleId: TEMP_MODULE_ID,
    label: 'Temp',
    scope: 'parent',
    summary: 'Preview-only parent sidebar exposure for staged giving and program route families.',
    dependencyMode: 'none',
    routePrefixes: [
      '/parent/portal/tip-a-teacher',
      '/parent/portal/donations',
      '/parent/portal/parnes-hayom',
      '/parent/portal/camps',
    ],
    routeGroups: [
      {
        routeGroupId: 'temp-parent-tip-a-teacher',
        label: 'Tip a Teacher',
        routePrefixes: ['/parent/portal/tip-a-teacher'],
      },
      {
        routeGroupId: 'temp-parent-donations',
        label: 'Donations',
        routePrefixes: ['/parent/portal/donations'],
      },
      {
        routeGroupId: 'temp-parent-parnes-hayom',
        label: 'Parnes HaYom',
        routePrefixes: ['/parent/portal/parnes-hayom'],
      },
      {
        routeGroupId: 'temp-parent-camps',
        label: 'Camps',
        routePrefixes: ['/parent/portal/camps'],
      },
    ],
    defaultEnabled: false,
    disableBehavior: 'hide-sidebar-only',
    showInEnablementUi: true,
  },
  {
    moduleId: 'resources',
    label: 'Resources',
    scope: 'parent',
    summary: 'Parent-facing handbooks, calendars, and shared resources.',
    dependencyMode: 'shared-data-source',
    routePrefixes: ['/parent/portal/resources'],
    routeGroups: [
      {
        routeGroupId: 'resources',
        label: 'Resources',
        routePrefixes: ['/parent/portal/resources'],
      },
    ],
    sidebarNavId: 'resources',
  },
] as const;

const PARENT_PORTAL_GOVERNED_FALLBACK_ORDER = [
  'dashboard',
  'applications',
  'enrollment',
  'financial-aid',
  'tuition',
  'calendar',
  'attendance',
  'academic',
  'resources',
] as const;

const PARENT_PORTAL_GOVERNED_ROUTE_MAP: Record<(typeof PARENT_PORTAL_GOVERNED_FALLBACK_ORDER)[number], string> = {
  dashboard: '/parent/portal/dashboard',
  applications: '/parent/portal/applications',
  enrollment: '/parent/portal/enrollment',
  'financial-aid': '/parent/portal/financialaid',
  tuition: '/parent/portal/tuition',
  calendar: '/parent/portal/calendar',
  attendance: '/parent/portal/attendance',
  academic: '/parent/portal/academic',
  resources: '/parent/portal/resources',
};

const normalizeRouteGroupOverrides = (
  routeGroups: ModuleGovernanceRouteGroup[] | undefined,
): Map<string, ModuleGovernanceRouteGroup> =>
  new Map(
    (routeGroups ?? [])
      .filter((routeGroup) => Boolean(routeGroup.routeGroupId))
      .map((routeGroup) => [routeGroup.routeGroupId, routeGroup]),
  );

const materializeModuleSet = (
  catalog: readonly ModuleGovernanceModuleCatalog[],
  persistedModules: ModuleGovernanceModule[] | undefined,
): MaterializedModuleGovernanceModule[] => {
  const persistedById = new Map(
    (persistedModules ?? [])
      .filter((module) => Boolean(module.moduleId))
      .map((module) => [module.moduleId, module]),
  );

  return catalog.map((catalogModule) => {
    const persistedModule = persistedById.get(catalogModule.moduleId);
    const routeGroupOverrides = normalizeRouteGroupOverrides(persistedModule?.routeGroups);
    const forceEnabled = isSessionForcedSchoolModule(catalogModule);
    return {
      ...catalogModule,
      enabled: catalogModule.alwaysOn
        ? true
        : forceEnabled
          ? true
        : catalogModule.retired
          ? false
          : persistedModule?.enabled ?? catalogModule.defaultEnabled ?? true,
      routeGroups: catalogModule.routeGroups.map((routeGroup) => ({
        ...routeGroup,
        enabled: forceEnabled
          ? true
          : catalogModule.retired
          ? false
          : routeGroupOverrides.get(routeGroup.routeGroupId)?.enabled ?? routeGroup.defaultEnabled ?? true,
      })),
    };
  });
};

const routeMatchesPrefix = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

export const MODULE_GOVERNANCE_CATALOG = {
  schoolModules: SCHOOL_MODULES,
  parentModules: PARENT_MODULES,
} as const;

const resolveCatalogModuleDefaultEnabled = (
  catalogModule: ModuleGovernanceModuleCatalog,
): boolean => {
  if (catalogModule.alwaysOn) {
    return true;
  }
  if (isSessionForcedSchoolModule(catalogModule)) {
    return true;
  }
  if (catalogModule.retired) {
    return false;
  }
  return catalogModule.defaultEnabled ?? true;
};

export const buildDefaultModuleGovernanceState = (): ModuleGovernanceState => ({
  schoolModules: SCHOOL_MODULES.map((catalogModule) => ({
    moduleId: catalogModule.moduleId,
    enabled: resolveCatalogModuleDefaultEnabled(catalogModule),
    routeGroups: catalogModule.routeGroups.map((routeGroup) => ({
      routeGroupId: routeGroup.routeGroupId,
      enabled: catalogModule.retired ? false : routeGroup.defaultEnabled ?? true,
    })),
  })),
  parentModules: PARENT_MODULES.map((catalogModule) => ({
    moduleId: catalogModule.moduleId,
    enabled: resolveCatalogModuleDefaultEnabled(catalogModule),
    routeGroups: catalogModule.routeGroups.map((routeGroup) => ({
      routeGroupId: routeGroup.routeGroupId,
      enabled: catalogModule.retired ? false : routeGroup.defaultEnabled ?? true,
    })),
  })),
});

export const materializeModuleGovernanceState = (
  rawGovernance: ModuleGovernanceState | null | undefined,
): MaterializedModuleGovernanceState => ({
  schoolModules: materializeModuleSet(SCHOOL_MODULES, rawGovernance?.schoolModules),
  parentModules: materializeModuleSet(PARENT_MODULES, rawGovernance?.parentModules),
});

export const isTempModuleEnabled = (
  governance: MaterializedModuleGovernanceState,
): boolean =>
  resolveEnabledSchoolTempNavIds(governance).size > 0
  || resolveEnabledParentTempNavIds(governance).size > 0;

const isSchoolModuleOperational = (
  governance: MaterializedModuleGovernanceState,
  schoolModuleId: string | undefined,
): boolean => {
  if (!schoolModuleId) {
    return true;
  }
  const schoolModule = governance.schoolModules.find((module) => module.moduleId === schoolModuleId);
  if (!schoolModule) {
    return false;
  }
  if (schoolModule.retired) {
    return false;
  }
  return schoolModule.enabled || Boolean(schoolModule.alwaysOn);
};

export const resolveEnabledSchoolSidebarNavIds = (
  governance: MaterializedModuleGovernanceState,
): Set<string> =>
  new Set(
    governance.schoolModules
      .filter((module) => module.enabled || module.alwaysOn)
      .map((module) => module.sidebarNavId)
      .filter((value): value is string => typeof value === 'string' && value.length > 0),
  );

export const resolveEnabledParentSidebarNavIds = (
  governance: MaterializedModuleGovernanceState,
): Set<string> => {
  const enabled = new Set<string>();
  for (const module of governance.parentModules) {
    if (
      !(module.enabled || module.alwaysOn)
      || !module.sidebarNavId
      || !isSchoolModuleOperational(governance, module.dependsOnSchoolModuleId)
    ) {
      continue;
    }
    enabled.add(module.sidebarNavId);
  }
  return enabled;
};

export const resolveGovernedParentPortalFallbackRoute = (
  enabledParentNavIds: ReadonlySet<string>,
): string | null => {
  if (!enabledParentNavIds.has('dashboard') && enabledParentNavIds.has('applications')) {
    return PARENT_PORTAL_GOVERNED_ROUTE_MAP.applications;
  }

  const fallbackNavId = PARENT_PORTAL_GOVERNED_FALLBACK_ORDER.find((navId) =>
    enabledParentNavIds.has(navId),
  );
  return fallbackNavId
    ? PARENT_PORTAL_GOVERNED_ROUTE_MAP[fallbackNavId]
    : null;
};

const resolveEnabledTempNavIdsForScope = (
  modules: MaterializedModuleGovernanceModule[],
  allowedRouteGroupIds: readonly string[],
): Set<string> => {
  const tempModule = modules.find((module) => module.moduleId === TEMP_MODULE_ID);
  if (!tempModule || !tempModule.enabled) {
    return new Set();
  }
  const allowedIds = new Set(allowedRouteGroupIds);
  return new Set(
    tempModule.routeGroups
      .filter((routeGroup) => routeGroup.enabled && allowedIds.has(routeGroup.routeGroupId))
      .map((routeGroup) => routeGroup.routeGroupId),
  );
};

export const resolveEnabledSchoolTempNavIds = (
  governance: MaterializedModuleGovernanceState,
): Set<string> => resolveEnabledTempNavIdsForScope(governance.schoolModules, SCHOOL_TEMP_ROUTE_GROUP_IDS);

export const resolveEnabledParentTempNavIds = (
  governance: MaterializedModuleGovernanceState,
): Set<string> => resolveEnabledTempNavIdsForScope(governance.parentModules, PARENT_TEMP_ROUTE_GROUP_IDS);

type GovernanceRouteTarget = {
  scope: GovernanceScope;
  module: MaterializedModuleGovernanceModule;
  routeGroup: MaterializedModuleGovernanceRouteGroup | null;
  matchedPrefix: string;
};

const findGovernedRouteTarget = (
  pathname: string,
  modules: MaterializedModuleGovernanceModule[],
  scope: GovernanceScope,
): GovernanceRouteTarget | null => {
  const matches: GovernanceRouteTarget[] = [];

  for (const module of modules) {
    for (const routeGroup of module.routeGroups) {
      for (const prefix of routeGroup.routePrefixes) {
        if (routeMatchesPrefix(pathname, prefix)) {
          matches.push({ scope, module, routeGroup, matchedPrefix: prefix });
        }
      }
    }
    for (const prefix of module.routePrefixes) {
      if (routeMatchesPrefix(pathname, prefix)) {
        matches.push({ scope, module, routeGroup: null, matchedPrefix: prefix });
      }
    }
  }

  return matches.sort((left, right) => right.matchedPrefix.length - left.matchedPrefix.length)[0] ?? null;
};

export const resolveGovernedRouteDisableReason = (
  pathname: string,
  governance: MaterializedModuleGovernanceState,
): string | null => {
  const target = findGovernedRouteTarget(pathname, governance.schoolModules, 'school')
    ?? findGovernedRouteTarget(pathname, governance.parentModules, 'parent');

  if (!target) return null;
  if (target.module.alwaysOn) return null;
  if (target.module.disableBehavior === 'hide-sidebar-only') {
    return null;
  }
  if (
    target.scope === 'parent'
    && target.module.dependsOnSchoolModuleId
    && !isSchoolModuleOperational(governance, target.module.dependsOnSchoolModuleId)
  ) {
    const schoolModule = governance.schoolModules.find(
      (module) => module.moduleId === target.module.dependsOnSchoolModuleId,
    );
    return `[YSTNJ routing] Parent module "${target.module.label}" is unavailable because school module "${schoolModule?.label ?? target.module.dependsOnSchoolModuleId}" is disabled.`;
  }
  if (!target.module.enabled) {
    return `[YSTNJ routing] Module "${target.module.label}" is disabled and hidden for this school.`;
  }
  if (target.routeGroup && !target.routeGroup.enabled) {
    return `[YSTNJ routing] Route group "${target.routeGroup.label}" in module "${target.module.label}" is disabled and hidden for this school.`;
  }
  return null;
};
